import React from "react";
import { useDashTheme } from "../../hooks/useDashTheme";

/**
 * OrbitSkeleton — the loading state for the Brain canvas.
 *
 * A generic grey block would be wrong here: the canvas is a radial graph, and a
 * rectangle tells the reader nothing about what is arriving. This is the SAME
 * shape as the thing being loaded — a pulsing core, concentric orbit rings, and
 * node placeholders on those rings — with a highlight sweeping across it.
 *
 * LIGHT MODE (the reason this was rewritten)
 * ──────────────────────────────────────────
 * The first version painted nodes in `surfaceHover` on a `bg` canvas. In dark
 * mode that is #303234 on #1D1F21 — clearly lighter, so it read fine. In light
 * mode it is #E3E7EE on #E9ECF1: a ~2% difference. The nodes were effectively
 * invisible, and the single blurred overlay band swept from a colour DARKER than
 * the canvas to one LIGHTER than it, which smeared rather than shimmered.
 *
 * The fix is to stop naming surface tokens and express the skeleton as CONTRAST
 * AGAINST WHATEVER THE CANVAS IS: a neutral ink at low alpha in light mode, a
 * neutral light at low alpha in dark mode. Both then hold the same visual weight
 * whichever theme is active.
 *
 * The shimmer is carried BY each shape (an animated gradient inside the node)
 * rather than by an overlay floating above them, so the highlight is guaranteed
 * to land on the placeholders instead of washing over the gap between them. A
 * per-node negative animation-delay derived from its x position makes the
 * highlight travel left-to-right across the whole orbit as one wave.
 *
 * Positions are deterministic (index-derived, no randomness) so the skeleton
 * does not jitter between renders. Honours prefers-reduced-motion.
 */

const RINGS = [
  { r: 0.20, n: 5, size: 30 },
  { r: 0.33, n: 8, size: 25 },
  { r: 0.455, n: 11, size: 20 },
];

const SWEEP_MS = 1600;

function useReducedMotion() {
  const read = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [reduce, setReduce] = React.useState(read);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = (e) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on);
    };
  }, []);
  return reduce;
}

/**
 * Skeleton ink, relative to the canvas rather than absolute.
 *   light → dark ink at low alpha   (placeholders sit BELOW the canvas)
 *   dark  → light ink at low alpha  (placeholders sit ABOVE the canvas)
 * Same perceived weight in both, which is what the token-named version failed at.
 */
function useSkeletonInk() {
  const t = useDashTheme();
  /* Alphas are NOT symmetric between the modes, deliberately. Dark ink on the
     light canvas (#E9ECF1) and light ink on the dark canvas (#1D1F21) do not
     travel at the same rate, so matching the alphas would leave light mode
     visibly fainter — which is exactly the fault being fixed. These were picked
     by measuring composited contrast against each canvas:
       dark  base 1.23 · peak 1.72
       light base 1.22 · peak 1.66     (was 1.05 — invisible) */
  return React.useMemo(() => (t.isDark
    ? { base: "rgba(255,255,255,0.07)", peak: "rgba(255,255,255,0.17)", ring: "rgba(255,255,255,0.12)", edge: "rgba(255,255,255,0.06)" }
    : { base: "rgba(17,24,39,0.10)", peak: "rgba(17,24,39,0.24)", ring: "rgba(17,24,39,0.14)", edge: "rgba(17,24,39,0.08)" }
  ), [t.isDark]);
}

const KEYFRAMES = `
@keyframes tgSkelSweep { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes tgSkelBreath { 0%,100% { transform: scale(1); } 50% { transform: scale(1.035); } }
@keyframes tgSkelRing { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
`;

export function OrbitSkeleton({ label = "Reading the window…" }) {
  const t = useDashTheme();
  const reduce = useReducedMotion();
  const ink = useSkeletonInk();

  const nodes = React.useMemo(() => {
    const out = [];
    RINGS.forEach((ring, ri) => {
      for (let i = 0; i < ring.n; i += 1) {
        // Offset each ring so nodes never line up into spokes.
        const angle = (i / ring.n) * Math.PI * 2 + ri * 0.42;
        out.push({
          key: `${ri}-${i}`,
          x: 50 + Math.cos(angle) * ring.r * 100,
          y: 50 + Math.sin(angle) * ring.r * 100,
          size: ring.size,
        });
      }
    });
    return out;
  }, []);

  /* One shimmer definition, reused by every shape. The highlight is INSIDE the
     shape, so it cannot miss it. */
  const shimmer = (delayFromX) => (reduce
    ? { background: ink.base }
    : {
      backgroundImage: `linear-gradient(100deg, ${ink.base} 38%, ${ink.peak} 50%, ${ink.base} 62%)`,
      backgroundSize: "300% 100%",
      animation: `tgSkelSweep ${SWEEP_MS}ms linear infinite`,
      // Negative delay by x position → the highlight travels across the orbit
      // as a single wave instead of every node pulsing in place.
      animationDelay: `-${Math.round((delayFromX / 100) * SWEEP_MS * 0.55)}ms`,
    });

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{
        position: "absolute", inset: 0, zIndex: 5, overflow: "hidden",
        background: t.c.bg, display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <style>{KEYFRAMES}</style>

      <div style={{ position: "relative", width: "min(560px, 72%)", aspectRatio: "1 / 1", maxHeight: "80%" }}>
        {/* orbit rings — solid hairlines, not dashes: a dashed ring at 10% alpha
            disappears entirely on a light canvas. */}
        {RINGS.map((ring, i) => (
          <span
            key={`ring-${i}`}
            style={{
              position: "absolute", left: "50%", top: "50%",
              width: `${ring.r * 200}%`, height: `${ring.r * 200}%`,
              marginLeft: `-${ring.r * 100}%`, marginTop: `-${ring.r * 100}%`,
              borderRadius: "50%", border: `1px solid ${ink.ring}`,
              opacity: reduce ? 0.7 : undefined,
              animation: reduce ? "none" : `tgSkelRing 2.6s ease-in-out ${i * 0.3}s infinite`,
            }}
          />
        ))}

        {/* the core */}
        <span style={{
          position: "absolute", left: "50%", top: "50%",
          width: 84, height: 84, marginLeft: -42, marginTop: -42,
          borderRadius: "50%", boxShadow: `0 0 0 1px ${ink.edge}`,
          animation: reduce ? "none" : `tgSkelBreath 2.4s ease-in-out infinite`,
          ...shimmer(50),
        }} />

        {/* node placeholders */}
        {nodes.map((n) => (
          <span
            key={n.key}
            style={{
              position: "absolute",
              left: `${n.x}%`, top: `${n.y}%`,
              width: n.size, height: n.size, marginLeft: -n.size / 2, marginTop: -n.size / 2,
              borderRadius: "50%", boxShadow: `0 0 0 1px ${ink.edge}`,
              ...shimmer(n.x),
            }}
          />
        ))}
      </div>

      <div style={{
        position: "absolute", bottom: 28, left: 0, right: 0, textAlign: "center",
        fontSize: t.font.size.sm, color: t.c.textTertiary, fontFamily: t.font.family,
      }}>
        {label}
      </div>
    </div>
  );
}

/** The list view's equivalent — same ink, same sweep, row shape. */
export function ListSkeleton({ rows = 7 }) {
  const t = useDashTheme();
  const reduce = useReducedMotion();
  const ink = useSkeletonInk();

  return (
    <div role="status" aria-live="polite" aria-label="Loading" style={{
      position: "absolute", inset: 0, zIndex: 5, background: t.c.bg,
      padding: `${t.s[6]}px ${t.s[6]}px`, overflow: "hidden",
    }}>
      <style>{KEYFRAMES}</style>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 54, borderRadius: t.r.md, marginBottom: t.s[3],
            boxShadow: `0 0 0 1px ${ink.edge}`,
            ...(reduce
              ? { background: ink.base }
              : {
                backgroundImage: `linear-gradient(100deg, ${ink.base} 38%, ${ink.peak} 50%, ${ink.base} 62%)`,
                backgroundSize: "300% 100%",
                animation: `tgSkelSweep ${SWEEP_MS}ms linear infinite`,
                animationDelay: `-${i * 90}ms`,
              }),
          }}
        />
      ))}
    </div>
  );
}

export default OrbitSkeleton;
